import { Editor } from '@ckeditor/ckeditor5-core/src/editor/editor';
import Position from '@ckeditor/ckeditor5-engine/src/model/position';
import TreeWalker from '@ckeditor/ckeditor5-engine/src/model/treewalker';
import { useBoolean } from 'ahooks';
import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Meeting } from 'api/meeting/types';
import { Button } from 'components/atoms/button';
import { AIFill20 } from 'components/atoms/icons/ai-fill';
import { Loading } from 'components/atoms/loading';
import {
  getAIFillAppId,
  getAIFillAppName,
} from 'components/organisms/ai-fill/utils';
import { useGetAiFillApps } from 'queries/ai-fill';
import { MeetingsDrawer } from './meetings-selector/meetings-selector';

const Icon = styled(AIFill20)``;
const Content = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  position: relative;
  &:after {
    content: '';
    position: absolute;
    height: 100%;
    border-right: 1px solid var(--coolest-gray-20);
    left: calc(20px + var(--spacing-03));
  }

  ${Icon} {
    flex-shrink: 0;
    margin-right: var(--spacing-03);
  }
`;
const Wrapper = styled.div`
  padding: var(--spacing-03);
  border: 1px solid var(--coolest-gray-20);
  background-color: var(--coolest-gray-10);
  border-radius: var(--border-radius-default);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

interface Props {
  appId: string;
  asReadOnly: boolean;
  uniqueId: string;
  editor: Editor;
}
export const AIFillApp: FC<Props> = ({ appId, editor, asReadOnly }) => {
  const { data: aiFillApps, isLoading: aiFillAppsLoading } = useGetAiFillApps();
  const selected = useMemo(
    () =>
      (aiFillApps ?? []).find(
        (app) => getAIFillAppId(app).toString() === appId,
      ),
    [aiFillApps, appId],
  );
  const [
    meetingsSelectorVisible,
    { setTrue: openMeetingsSelector, setFalse: closeMeetingsSelector },
  ] = useBoolean(false);
  const onGenerate = useCallback(
    (meetings: Meeting[]) => {
      closeMeetingsSelector();
      const sourceMeetingIds = meetings.map(({ meetingId }) => meetingId);
      const rootBeginning = new Position(editor.model.document.getRoot(), [0]);
      const iterator = new TreeWalker({ startPosition: rootBeginning });

      [...iterator].forEach((value) => {
        if (value.type === 'elementEnd' && value.item.name === 'aiFill') {
          editor.execute('ai-fill-resolve', {
            appId: value.item.getAttribute('appId'),
            uniqueId: value.item.getAttribute('uniqueId'),
            sourceMeetingIds,
          });
        }
      }, []);
    },
    [closeMeetingsSelector, editor],
  );

  return (
    <Wrapper>
      <Content>
        <Icon />
        {aiFillAppsLoading ? (
          <Loading />
        ) : (
          selected && getAIFillAppName(selected)
        )}
      </Content>
      {asReadOnly === true ? null : (
        <Button type="link" size="small" onClick={openMeetingsSelector}>
          <FormattedMessage id="generate" />
        </Button>
      )}
      {meetingsSelectorVisible ? (
        <MeetingsDrawer
          onClose={closeMeetingsSelector}
          onGenerate={onGenerate}
        />
      ) : null}
    </Wrapper>
  );
};
